import { AnimatePresence, Transition, Variants, motion } from "framer-motion";
import React, { FC, Fragment, useRef } from "react";
import { Show, TextArea } from "components/core";
import { classNames, wrapClick } from "utils";

import { Dialog } from "@headlessui/react";
import { FormikProps } from "formik";
import Loader from "./loader";
import { XMarkIcon } from "@heroicons/react/24/outline";

type Placement = "top-center" | "bottom-center" | "top-right" | "bottom-right" | "top-left" | "bottom-left";

type Props = {
  title: string;
  description?: string;
  open: boolean;
  cancelText?: string;
  setOpen: (val: boolean) => void;
  renderActions?: () => JSX.Element;
  loading?: boolean;
  disabled?: boolean;
  variant?: "danger" | "success" | "warning" | "info";
  reason?: string;
  form?: FormikProps<any>;
  placement?: Placement;
  hideCloseButton?: boolean;
};

const variantMap = {
  danger: "focus:ring-red-500 text-red-500",
  success: "focus:ring-green-500 text-green-500",
  warning: "focus:ring-yellow-500 text-yellow-500",
  info: "focus:ring-blue-500 text-blue-500",
};

const placementClasses: Record<Placement, string> = {
  "top-center": "items-start justify-center",
  "bottom-center": "items-end justify-center",
  "top-right": "items-start justify-end",
  "bottom-right": "items-end justify-end",
  "top-left": "items-start justify-start",
  "bottom-left": "items-end justify-start",
};

const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
};

const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 500,
    } as Transition,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.2,
    } as Transition,
  },
};

const PopUp: FC<Props> = ({
  open,
  setOpen,
  renderActions,
  title,
  cancelText,
  description,
  loading,
  disabled,
  variant = "danger",
  reason,
  form,
  placement = "top-right",
  hideCloseButton = false,
}) => {
  const cancelButtonRef = useRef(null);
  const containerBaseClasses = "flex min-h-full p-4 text-center";
  const containerPlacementClasses = placementClasses[placement];

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          as="div"
          className={classNames("theme", "fixed z-50 inset-0 overflow-y-auto")}
          initialFocus={cancelButtonRef}
          onClose={setOpen}
          open={open}
          static
        >
          <div className={classNames(containerBaseClasses, containerPlacementClasses)}>
            {/* Backdrop with Framer Motion */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={backdropVariants}
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
            />

            {/* Modal Panel with Framer Motion */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              className={classNames(
                "max-w-2xl",
                "inline-block z-50 bg-white rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:w-full"
              )}
            >
              {/* Rest of your component remains the same */}
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  onClick={() => setOpen(false)}
                >
                  <span className="sr-only">{cancelText ?? "Cancel"}</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              {loading ? (
                <div className="p-4">
                  <Loader color="red" />
                </div>
              ) : (
                <>
                  <div className="sm:flex sm:flex-col">
                    <div className="mx-auto pt-4 flex-shrink-0 flex flex-col items-center rounded sm:mx-0 sm:px-6 sm:py-4 border-gray-200">
                      <Dialog.Title
                        as="h2"
                        className="flex-1 mt-4 text-lg leading-6 font-bold text-gray-900 text-center sm:text-center"
                      >
                        {title}
                      </Dialog.Title>
                      {description && (
                        <p className="w-full mt-1 text-center text-base text-gray-600 sm:w-auto sm:text-sm">
                          {description}
                        </p>
                      )}
                      {reason && form && (
                        <TextArea
                          id="note"
                          className="w-full mt-2 p-2 rounded-md"
                          placeholder="Enter your reason here..."
                          {...form}
                          label=""
                        />
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-3 space-x-3 justify-end items-center sm:py-4 sm:px-6 sm:flex">
                    <>{renderActions?.()}</>
                    <Show if={!hideCloseButton}>
                      <button
                        type="button"
                        disabled={disabled}
                        className={classNames(
                          "w-full inline-flex disabled:bg-gray-400 justify-center rounded-md border border-gray-300 shadow-sm px-4 py-1.5 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm",
                          variantMap[variant]
                        )}
                        onClick={wrapClick(() => setOpen(false))}
                        ref={cancelButtonRef}
                      >
                        {cancelText ?? "Cancel"}
                      </button>
                    </Show>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default PopUp;